import type { AssetMode, AssetSortField, AssetType, SortOrder } from "../types";
import {
    type CreatorOptionSource,
    type FilterOption,
    dateRangeToBounds,
    draftToFilterArrays,
    mapCreatorOptions,
    singleValueDraft,
} from "~/shared/lib/filter-utils";

export type AssetTab = "all" | "images" | "videos" | "audio" | "folders";

/** Вкладки-типы списка ассетов; `folders` — папки, реализованные командным ревью. */
export const ASSET_TABS: readonly AssetTab[] = ["all", "images", "videos", "audio", "folders"];

/** Все режимы генерации (ArtifactGenerationModeEnum) — опции фильтра «режим». */
export const ASSET_MODES: readonly AssetMode[] = [
    "text_to_video",
    "text_to_image",
    "frames",
    "references",
    "image_to_video",
    "edit",
    "motion_control",
    "omni_reference",
    "upscale_image",
    "upscale_video",
];

export type AssetsFilters = {
    model: readonly string[];
    mode: readonly string[];
    project: readonly string[];
    scene: readonly string[];
    creator: readonly string[];
    createdAt: readonly string[];
};

export const DEFAULT_ASSETS_FILTERS: AssetsFilters = {
    model: [],
    mode: [],
    project: [],
    scene: [],
    creator: [],
    createdAt: [],
};

const ASSETS_FILTER_KEYS = Object.keys(DEFAULT_ASSETS_FILTERS) as (keyof AssetsFilters)[];

/** URL filters (arrays) → single-value draft for the filter panel. */
export function assetsDraftFromFilters(filters: AssetsFilters): Record<string, unknown> {
    return singleValueDraft(filters);
}

/** Single-value draft → URL filter arrays, written in ONE batched update (see chats precedent). */
export function applyAssetsDraft(
    draft: Record<string, unknown>,
    setFilters: (entries: Partial<Record<keyof AssetsFilters, readonly string[]>>) => void,
): void {
    setFilters(draftToFilterArrays<AssetsFilters>(draft, ASSETS_FILTER_KEYS));
}

export { type CreatorOptionSource, type FilterOption, dateRangeToBounds, mapCreatorOptions };

/** Active type tab → backend `type` filter (`all`/`folders` → no type constraint). */
export function assetTypeForTab(tab: AssetTab): AssetType | undefined {
    if (tab === "images") return "image";
    if (tab === "videos") return "video";
    if (tab === "audio") return "audio";
    return undefined;
}

// Column id → backend sort_by whitelist. Only these two columns are sortable.
const ASSET_SORT_BY: Record<string, AssetSortField> = {
    createdAt: "created_at",
    modelName: "model_name",
};

/** Table sort column id → API `sort_by` (undefined → backend default `created_at`). */
export function assetSortBy(sortId: string | undefined): AssetSortField | undefined {
    return sortId ? ASSET_SORT_BY[sortId] : undefined;
}

/** Table sort state → API order direction (undefined when unsorted). */
export function assetOrder(sort: { desc: boolean } | undefined): SortOrder | undefined {
    if (!sort) return undefined;
    return sort.desc ? "desc" : "asc";
}
